import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export default function useTargetFigureNavigation({
  targetFigure,
  pdfDocumentProxy,
  pageRefs,
  containerRef,
  textLayerReadyPagesRef,
  isProgrammaticScrollRef,
  setCurrentPage,
}) {
  const [activeFigureHighlight, setActiveFigureHighlight] = useState(null);
  const lastTargetFigureRef = useRef(null);
  const pendingTargetFigureRef = useRef(null);

  const isFigureDebugEnabled = useCallback(() => {
    if (typeof window === 'undefined') return false;
    try {
      return Boolean(window.__PDF_FIGURE_DEBUG__) || localStorage.getItem('pdf_figure_debug') === '1';
    } catch {
      return Boolean(window.__PDF_FIGURE_DEBUG__);
    }
  }, []);

  const figDebug = useCallback((...args) => {
    if (!isFigureDebugEnabled()) return;
    console.log('[FIGURE_DEBUG]', ...args);
  }, [isFigureDebugEnabled]);

  const setFigureOverlayFromDOM = useCallback((
    pageNum,
    {
      itemIndex = null,
      targetText = null,
      figureNumber = null,
      preferredXPct = null,
      preferredYPct = null,
    } = {},
    attempts = 0
  ) => {
    try {
      const pageContainer = pageRefs.current?.[pageNum];
      const pageElement = pageContainer?.querySelector?.('.react-pdf__Page');
      const textLayer = pageElement?.querySelector?.('.react-pdf__Page__textContent');
      const spans = textLayer?.querySelectorAll?.('span');

      if (!textLayerReadyPagesRef.current?.[pageNum]) {
        if (attempts < 60) {
          setTimeout(() => setFigureOverlayFromDOM(pageNum, { itemIndex, targetText, figureNumber, preferredXPct, preferredYPct }, attempts + 1), 100);
        }
        return false;
      }

      if (!pageElement || !textLayer || !spans || spans.length === 0) {
        if (attempts < 60) {
          setTimeout(() => setFigureOverlayFromDOM(pageNum, { itemIndex, targetText, figureNumber, preferredXPct, preferredYPct }, attempts + 1), 100);
        }
        return false;
      }

      if (spans.length < 20 && attempts < 60) {
        setTimeout(() => setFigureOverlayFromDOM(pageNum, { itemIndex, targetText, figureNumber, preferredXPct, preferredYPct }, attempts + 1), 100);
        return false;
      }

      const pageRect = pageElement.getBoundingClientRect();
      if (!pageRect?.width || !pageRect?.height) {
        if (attempts < 60) {
          setTimeout(() => setFigureOverlayFromDOM(pageNum, { itemIndex, targetText, figureNumber, preferredXPct, preferredYPct }, attempts + 1), 100);
        }
        return false;
      }

      const clampPct = (v) => Math.max(0, Math.min(100, v));

      const normalize = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const normalizedTarget = normalize(targetText);

      const isCaptionLike = (raw) => /^\s*(fig\.?|figure|figs\.?|figures)\b/i.test(String(raw || ''));

      const hasFigureKeyword = (raw) => /\b(fig\.?|figure|figs\.?|figures)\b/i.test(String(raw || ''));

      const captionStartsWithFigureNumber = (raw) => {
        if (!figureNumber) return false;
        const escaped = String(figureNumber).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`^\\s*(?:fig\\.?|figure|figs\\.?|figures)\\s*${escaped}\\s*[:]`, 'i').test(String(raw || ''));
      };

      const isLikelyInTextReference = (raw) => {
        const norm = normalize(raw);
        return /\b(as\s+shown\s+in|shown\s+in|see|refer\s+to|in)\b.*\b(fig|figure)\b/.test(norm);
      };

      const scoreSpan = (spanEl) => {
        const raw = spanEl?.textContent || '';
        const norm = normalize(raw);
        let score = 0;
        if (normalizedTarget && norm === normalizedTarget) score += 200;
        if (normalizedTarget && norm.includes(normalizedTarget)) score += 120;
        if (figureNumber && norm.includes(String(figureNumber))) score += 80;
        if (isCaptionLike(raw)) score += 60;
        if (captionStartsWithFigureNumber(raw)) score += 1200;
        if (isLikelyInTextReference(raw)) score -= 800;
        return score;
      };

      const shouldAcceptDomWinner = (winner) => {
        if (!winner) return false;

        if (winner.kind === 'window' || winner.kind === 'spatial-line') return true;

        const text = String(winner.windowText || winner.text || winner.spanEl?.textContent || '');
        const xDist = Number.isFinite(winner.xDist)
          ? winner.xDist
          : (Number.isFinite(preferredXPct) && Number.isFinite(winner.xPct))
            ? Math.abs(winner.xPct - preferredXPct)
            : null;
        const yDist = Number.isFinite(winner.yDist)
          ? winner.yDist
          : (Number.isFinite(preferredYPct) && Number.isFinite(winner.yPct))
            ? Math.abs(winner.yPct - preferredYPct)
            : null;

        const closeEnoughY = !Number.isFinite(preferredYPct) || (Number.isFinite(yDist) && yDist <= 6);

        const containsFigureNumber = figureNumber ? normalize(text).includes(String(figureNumber)) : false;
        const looksCaptionish = captionStartsWithFigureNumber(text) || (hasFigureKeyword(text) && containsFigureNumber);

        const scoreHigh = (winner.score ?? 0) >= 300;
        const scoreVeryHigh = (winner.score ?? 0) >= 800;

        if (looksCaptionish && (scoreHigh || scoreVeryHigh)) return true;
        if (scoreVeryHigh) return true;
        if (scoreHigh && closeEnoughY) return true;

        figDebug('DOM winner rejected (keeping PDF coords)', {
          page: pageNum,
          kind: winner.kind,
          score: winner.score,
          xPct: winner.xPct,
          yPct: winner.yPct,
          xDist,
          yDist,
          preferredXPct,
          preferredYPct,
          text: text.substring(0, 80),
        });

        return false;
      };

      const unionRects = (rects) => {
        const valid = (rects || []).filter(r => r && Number.isFinite(r.left) && Number.isFinite(r.top) && Number.isFinite(r.right) && Number.isFinite(r.bottom));
        if (valid.length === 0) return null;
        const left = Math.min(...valid.map(r => r.left));
        const top = Math.min(...valid.map(r => r.top));
        const right = Math.max(...valid.map(r => r.right));
        const bottom = Math.max(...valid.map(r => r.bottom));
        return { left, top, right, bottom, width: Math.max(1, right - left), height: Math.max(1, bottom - top) };
      };

      const spanArray = Array.from(spans);
      const maxWindow = 8;
      let best = null;
      const allCandidates = [];

      const spanInfos = spanArray.map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          el,
          rect,
          text: el?.textContent || '',
          xPct: ((rect.left - pageRect.left) / pageRect.width) * 100,
          yPct: ((rect.top - pageRect.top) / pageRect.height) * 100,
        };
      });

      const considerCandidate = (candidate) => {
        if (!candidate) return;
        allCandidates.push(candidate);
        if (!best || candidate.score > best.score) best = candidate;
      };

      if (figureNumber) {
        const escaped = String(figureNumber).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const captionPrefixRegex = new RegExp(`^\\s*(?:fig\\.?|figure|figs\\.?|figures)\\s*${escaped}(?:\\b|\\s|[.:])`, 'i');
        const hasColonAfterNumberRegex = new RegExp(`^\\s*(?:fig\\.?|figure|figs\\.?|figures)\\s*${escaped}\\s*[:]`, 'i');

        for (const seed of spanInfos) {
          const seedText = String(seed.text || '').trim();
          if (!/^\s*(fig\.?|figure|figs\.?|figures)\b/i.test(seedText)) continue;

          const yTol = Math.max(3, Math.min(10, (seed.rect?.height || 0) * 0.6));
          const sameLine = spanInfos
            .filter((s) => {
              if (!s?.rect) return false;
              const dy = Math.abs((s.rect.top || 0) - (seed.rect.top || 0));
              return dy <= yTol;
            })
            .sort((a, b) => (a.rect.left || 0) - (b.rect.left || 0));

          const startIdx = sameLine.findIndex((s) => s.el === seed.el);
          if (startIdx < 0) continue;

          let text = '';
          const chosen = [];
          for (let k = startIdx; k < sameLine.length && chosen.length < 6; k++) {
            const seg = String(sameLine[k]?.text || '').trim();
            if (!seg) continue;
            text = (text + ' ' + seg).trim();
            chosen.push(sameLine[k]);
            if (captionPrefixRegex.test(text)) break;
          }

          if (!captionPrefixRegex.test(text)) continue;

          const union = unionRects(chosen.map((s) => s.rect));
          if (!union) continue;

          const xPct = ((union.left - pageRect.left) / pageRect.width) * 100;
          const yPct = ((union.top - pageRect.top) / pageRect.height) * 100;

          let score = hasColonAfterNumberRegex.test(text) ? 2000 : 1500;
          const xDist = Number.isFinite(preferredXPct) ? Math.abs(xPct - preferredXPct) : 0;
          const yDist = Number.isFinite(preferredYPct) ? Math.abs(yPct - preferredYPct) : 0;
          if (Number.isFinite(preferredXPct)) score -= xDist * 0.2;
          if (Number.isFinite(preferredYPct)) score -= yDist * 0.1;

          figDebug('spatial-line candidate:', { text, score, xPct, yPct, xDist, yDist, hasColon: hasColonAfterNumberRegex.test(text) });
          considerCandidate({ kind: 'spatial-line', score, rect: union, windowText: text, xPct, yPct, xDist, yDist });
        }
      }

      for (let idx = 0; idx < spanArray.length; idx++) {
        const spanEl = spanArray[idx];
        const baseScore = scoreSpan(spanEl);
        if (baseScore > 0) {
          const rect = spanEl.getBoundingClientRect();
          const xPct = ((rect.left - pageRect.left) / pageRect.width) * 100;
          const yPct = ((rect.top - pageRect.top) / pageRect.height) * 100;
          let score = baseScore;
          const xDist = Number.isFinite(preferredXPct) ? Math.abs(xPct - preferredXPct) : 0;
          const yDist = Number.isFinite(preferredYPct) ? Math.abs(yPct - preferredYPct) : 0;
          if (Number.isFinite(preferredXPct)) score -= xDist * 2;
          if (Number.isFinite(preferredYPct)) score -= yDist * 1.5;
          figDebug('span candidate:', { text: spanEl.textContent?.substring(0, 50), baseScore, score, xPct, yPct, xDist, yDist });
          considerCandidate({ kind: 'span', spanEl, score, rect, xPct, yPct, xDist, yDist, text: spanEl.textContent });
        }

        if (!figureNumber) continue;

        const windowSpans = [spanEl];
        let windowText = spanEl?.textContent || '';
        for (let k = 1; k < maxWindow && idx + k < spanArray.length; k++) {
          const nextEl = spanArray[idx + k];
          const nextText = nextEl?.textContent || '';
          if (!nextText && k > 1) break;
          windowText = (windowText + ' ' + nextText).trim();
          windowSpans.push(nextEl);

          if (captionStartsWithFigureNumber(windowText)) break;
        }

        const windowLooksCaption = captionStartsWithFigureNumber(windowText);
        if (!windowLooksCaption) continue;

        const rects = windowSpans.map(s => s.getBoundingClientRect());
        const union = unionRects(rects);
        if (!union) continue;
        const xPct = ((union.left - pageRect.left) / pageRect.width) * 100;
        const yPct = ((union.top - pageRect.top) / pageRect.height) * 100;
        let score = 1800;
        const xDist = Number.isFinite(preferredXPct) ? Math.abs(xPct - preferredXPct) : 0;
        const yDist = Number.isFinite(preferredYPct) ? Math.abs(yPct - preferredYPct) : 0;
        if (Number.isFinite(preferredXPct)) score -= xDist * 2;
        if (Number.isFinite(preferredYPct)) score -= yDist * 1.5;
        figDebug('window candidate:', { windowText, score, xPct, yPct, xDist, yDist });
        considerCandidate({ kind: 'window', score, rect: union, windowText, xPct, yPct, xDist, yDist });
      }

      if (!best && Number.isFinite(itemIndex)) {
        const fallbackEl = spanArray[itemIndex] || null;
        if (fallbackEl) {
          considerCandidate({ kind: 'span', spanEl: fallbackEl, score: 1, rect: fallbackEl.getBoundingClientRect() });
        }
      }

      if (!best?.rect) {
        figDebug('No valid candidate found', { totalCandidates: allCandidates.length, attempts });
        if (attempts < 30) {
          setTimeout(() => setFigureOverlayFromDOM(pageNum, { itemIndex, targetText, figureNumber, preferredXPct, preferredYPct }, attempts + 1), 100);
        }
        return false;
      }

      if (!shouldAcceptDomWinner(best)) {
        if (attempts < 60) {
          setTimeout(() => setFigureOverlayFromDOM(pageNum, { itemIndex, targetText, figureNumber, preferredXPct, preferredYPct }, attempts + 1), 120);
        }
        return false;
      }

      figDebug('WINNER:', {
        kind: best.kind,
        score: best.score,
        xPct: best.xPct,
        yPct: best.yPct,
        text: best.windowText || best.text || best.spanEl?.textContent?.substring(0, 50),
        totalCandidates: allCandidates.length,
        allScores: allCandidates.map(c => ({ kind: c.kind, score: c.score, text: (c.windowText || c.text || c.spanEl?.textContent)?.substring(0, 30) }))
      });

      const chosenRect = best.rect;
      const xPct = ((chosenRect.left - pageRect.left) / pageRect.width) * 100;
      const yPct = ((chosenRect.top - pageRect.top) / pageRect.height) * 100;
      const wPct = (chosenRect.width / pageRect.width) * 100;
      const hPct = (chosenRect.height / pageRect.height) * 100;

      setActiveFigureHighlight({
        pageNumber: pageNum,
        x: Number.isFinite(xPct) ? clampPct(xPct) : 0,
        y: Number.isFinite(yPct) ? clampPct(yPct) : 0,
        width: Number.isFinite(wPct) ? clampPct(wPct) : 0,
        height: Number.isFinite(hPct) ? clampPct(hPct) : 0,
      });

      figDebug('activeFigureHighlight set (DOM)', {
        page: pageNum,
        itemIndex,
        targetText,
        figureNumber,
        xPct,
        yPct,
        wPct,
        hPct,
        kind: best?.kind,
        spanText: best?.spanEl?.textContent,
        windowText: best?.windowText,
        score: best?.score ?? null,
      });

      return true;
    } catch {
      return false;
    }
  }, [figDebug, pageRefs, textLayerReadyPagesRef]);

  useEffect(() => {
    if (!targetFigure) {
      pendingTargetFigureRef.current = null;
      lastTargetFigureRef.current = null;
      figDebug('targetFigure cleared; reset dedupe');
      return;
    }

    if (!pdfDocumentProxy) {
      pendingTargetFigureRef.current = targetFigure;
      console.log('🖼️ Figure requested; waiting for PDF to load:', targetFigure);
      figDebug('queued targetFigure awaiting pdfDocumentProxy', { targetFigure });
      return;
    }

    const figureToFind = pendingTargetFigureRef.current || targetFigure;
    pendingTargetFigureRef.current = null;

    const normalizedTargetKey = String(figureToFind || '').trim().toLowerCase();
    if (lastTargetFigureRef.current === normalizedTargetKey) return;
    lastTargetFigureRef.current = normalizedTargetKey;

    figDebug('targetFigure received:', figureToFind);
    console.log('🖼️ Searching for figure:', figureToFind);

    const findAndHighlightFigure = async () => {
      try {
        if (!pdfDocumentProxy) return;
        const numPages = pdfDocumentProxy.numPages;

        figDebug('pdfDocumentProxy ready', { numPages, hasPageRefs: !!pageRefs?.current, hasContainer: !!containerRef?.current });

        const normalizeFigureText = (value) => {
          return String(value || '')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .replace(/\bfigs\.?\b/g, 'figure')
            .replace(/\bfig\.?\b/g, 'figure')
            .trim();
        };

        const normalizedTarget = normalizeFigureText(figureToFind);

        const figureNumberMatch = String(figureToFind || '').match(/(\d+(?:\.\d+)?)/);
        const figureNumber = figureNumberMatch ? figureNumberMatch[0] : null;

        console.log(`🖼️ Searching for figure: "${figureToFind}" (Number: ${figureNumber})`);

        const candidates = [];
        const escapedFigureNumber = figureNumber ? figureNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : null;

        for (let i = 1; i <= numPages; i++) {
          const page = await pdfDocumentProxy.getPage(i);
          const viewport = page.getViewport({ scale: 1.0 });

          if (!viewport) {
            console.warn(`[PDF] Viewport not defined for page ${i}`);
            continue;
          }

          const textContent = await page.getTextContent();
          const textItems = textContent.items;

          if (i === 1) {
            figDebug('sample text items (page 1)', {
              count: textItems?.length,
              first: textItems?.[0]?.str,
              second: textItems?.[1]?.str,
            });
          }

          const figureLabelPrefix = figureNumber ? `figure ${figureNumber}` : null;
          const startsWithLabelRegex = escapedFigureNumber
            ? new RegExp(`^\\s*(?:fig\\.?|figure|figs\\.?|figures)\\s*${escapedFigureNumber}(?:\\b|\\s|[.:])`, 'i')
            : null;

          for (let j = 0; j < textItems.length; j++) {
            const raw = String(textItems[j]?.str || '').trim();
            if (!raw) continue;

            const prevRaw = j > 0 ? String(textItems[j - 1]?.str || '') : '';
            const combinedRaw = (prevRaw + ' ' + raw).trim();

            const normalizedStr = normalizeFigureText(raw);
            const normalizedCombined = normalizeFigureText(combinedRaw);

            let score = 0;

            if (figureLabelPrefix) {
              if (normalizedStr.startsWith(figureLabelPrefix)) score = Math.max(score, 100);
              if (normalizedCombined.startsWith(figureLabelPrefix)) score = Math.max(score, 95);
              if (normalizedStr.includes(figureLabelPrefix)) score = Math.max(score, 60);
              if (normalizedCombined.includes(figureLabelPrefix)) score = Math.max(score, 55);

              if (startsWithLabelRegex && (startsWithLabelRegex.test(raw) || startsWithLabelRegex.test(combinedRaw))) {
                score = Math.max(score, 110);
              }

              if (!normalizedStr.startsWith(figureLabelPrefix)) {
                if (/\b(as\s+shown\s+in|shown\s+in|see|refer\s+to|in)\b.*\bfigure\b/.test(normalizedStr)) {
                  score -= 25;
                }
              }
            } else {
              if (normalizedStr.includes(normalizedTarget)) score = Math.max(score, 50);
              if (normalizedCombined.includes(normalizedTarget)) score = Math.max(score, 45);
            }

            if (score <= 0) continue;

            if (score > 0) {
              figDebug(`page ${i} item ${j}: score=${score}`, { raw: raw.substring(0, 60), normalizedStr: normalizedStr.substring(0, 60) });
            }

            candidates.push({
              score,
              pageNumber: i,
              itemIndex: j,
              item: textItems[j],
              viewport,
              raw,
              normalizedStr,
              combinedRaw,
              normalizedCombined,
            });

            if (score >= 110) break;
          }

          if (candidates.some(c => c.pageNumber === i && c.score >= 110)) {
            break;
          }
        }

        figDebug('Candidate summary:', {
          totalCandidates: candidates.length,
          topScores: candidates.slice(0, 5).map(c => ({ page: c.pageNumber, score: c.score, text: c.raw.substring(0, 40) }))
        });

        const best = candidates.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.pageNumber - b.pageNumber;
        })[0];

        if (best) {
          const i = best.pageNumber;
          const foundItem = best.item;
          const foundIndex = best.itemIndex;
          const viewport = best.viewport;

          console.log(`✅ Found figure on page ${i}:`, foundItem.str);
          figDebug('FOUND', {
            page: i,
            foundText: foundItem?.str,
            foundIndex,
            score: best.score,
            matchedRaw: best.raw,
            matchedCombinedRaw: best.combinedRaw,
            hasTransform: Array.isArray(foundItem?.transform),
            width: foundItem?.width,
            height: foundItem?.height,
          });

          isProgrammaticScrollRef.current = true;
          setCurrentPage(i);

          const attemptScroll = (attempts = 0) => {
            const pageContainer = pageRefs.current[i];
            const container = containerRef.current?.closest('.overflow-auto') || containerRef.current;
            if (!pageContainer || !container) {
              if (attempts < 30) {
                setTimeout(() => attemptScroll(attempts + 1), 100);
              } else {
                isProgrammaticScrollRef.current = false;
              }
              return;
            }

            pageContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });

            setTimeout(() => {
              isProgrammaticScrollRef.current = false;
            }, 700);
          };

          requestAnimationFrame(() => {
            requestAnimationFrame(() => attemptScroll());
          });

          const tx = Number(foundItem?.transform?.[4]);
          const ty = Number(foundItem?.transform?.[5]);
          const itemWidth = Number(foundItem?.width) || 0;
          const itemHeight = Number(foundItem?.height) || 0;

          let left = tx;
          let top = ty;
          let right = tx + itemWidth;
          let bottom = ty + itemHeight;

          try {
            if (viewport?.convertToViewportPoint && Number.isFinite(tx) && Number.isFinite(ty)) {
              const p1 = viewport.convertToViewportPoint(tx, ty);
              const p2 = viewport.convertToViewportPoint(tx + (itemWidth || 1), ty);
              const p3 = viewport.convertToViewportPoint(tx, ty + (itemHeight || 1));
              const x1 = Number(p1?.[0]);
              const y1 = Number(p1?.[1]);
              const x2 = Number(p2?.[0]);
              const y2 = Number(p2?.[1]);
              const x3 = Number(p3?.[0]);
              const y3 = Number(p3?.[1]);

              if ([x1, y1].every(Number.isFinite)) {
                left = x1;
                top = y1;
              }
              if ([x1, x2].every(Number.isFinite)) {
                left = Math.min(x1, x2);
                right = Math.max(x1, x2);
              }
              if ([y1, y3].every(Number.isFinite)) {
                top = Math.min(y1, y3);
                bottom = Math.max(y1, y3);
              }

              if (!Number.isFinite(right) || right <= left) right = left + 8;
              if (!Number.isFinite(bottom) || bottom <= top) bottom = top + 12;
            }
          } catch (e) {
            console.warn('[PDF] Viewport conversion failed, using raw coordinates', e);
          }

          const safeWidth = Math.max(1, right - left);
          const safeHeight = Math.max(1, bottom - top);

          const xPercent = (left / viewport.width) * 100;
          const yPercent = (top / viewport.height) * 100;

          setActiveFigureHighlight({
            pageNumber: i,
            x: Number.isFinite(xPercent) ? Math.max(0, Math.min(100, xPercent)) : 0,
            y: Number.isFinite(yPercent) ? Math.max(0, Math.min(100, yPercent)) : 0,
            width: (safeWidth / viewport.width) * 100,
            height: (safeHeight / viewport.height) * 100
          });

          figDebug('activeFigureHighlight set', {
            page: i,
            xPct: xPercent,
            yPct: yPercent,
            wPct: (safeWidth / viewport.width) * 100,
            hPct: (safeHeight / viewport.height) * 100,
          });

          requestAnimationFrame(() => {
            setTimeout(() => {
              setFigureOverlayFromDOM(
                i,
                {
                  itemIndex: foundIndex,
                  targetText: foundItem?.str,
                  figureNumber,
                  preferredXPct: Number.isFinite(xPercent) ? xPercent : null,
                  preferredYPct: Number.isFinite(yPercent) ? yPercent : null,
                }
              );
            }, 150);
          });

          setTimeout(() => {
            setActiveFigureHighlight(null);
            figDebug('cleared figure highlight');
          }, 5000);

          return;
        }

        console.warn('⚠️ Figure not found:', figureToFind);
        figDebug('NOT FOUND', { targetFigure: figureToFind, normalizedTarget, figureNumber });
        toast.error(`Could not locate ${figureToFind} in this document`);
      } catch (err) {
        console.error('Error finding figure:', err);
      }
    };

    findAndHighlightFigure();
  }, [targetFigure, pdfDocumentProxy, figDebug, setFigureOverlayFromDOM, pageRefs, containerRef, isProgrammaticScrollRef, setCurrentPage]);

  return {
    activeFigureHighlight,
  };
}
